import { AnimatePresence, motion } from 'motion/react'
import styled, { css } from 'styled-components'

import { mediaMinTablet } from '../theme'
import { useValueContext } from '../value-context'

import type { ValueContextValue } from '../value-context'
import type { ClassNameProps } from '@root/shared/react'

type ContextValue = ValueContextValue<{
  indicatorPosition: IndicatorPosition
}>
type IndicatorPosition = 'top' | 'bottom'

type ItemProps = {
  value: string
  onClick?: () => void
  disabled?: boolean
} & React.PropsWithChildren &
  ClassNameProps

export const UnderlineTabsTab: React.FC<ItemProps> = ({
  children,
  value,
  onClick,
  className,
  disabled,
}) => {
  const ctx = useValueContext() as ContextValue
  const isActive = ctx.value === value

  return (
    <ItemRoot
      className={className}
      data-active={isActive}
      aria-disabled={disabled}
      type="button"
      onClick={() => {
        if (onClick) {
          onClick()
        } else {
          ctx.onChange(value)
        }
      }}
    >
      <span>{children}</span>
      <AnimatePresence>
        {isActive ? (
          <Indicator
            data-position={ctx.indicatorPosition}
            transition={{ duration: 0.15 }}
            layoutId={ctx.layoutId}
            layout
            key="indicator"
          />
        ) : undefined}
      </AnimatePresence>
    </ItemRoot>
  )
}

const ItemRoot = styled.button`
  all: unset;
  position: relative;
  display: grid;
  justify-content: center;
  align-items: center;

  text-align: center;
  transition: all 0.15s linear;
  cursor: pointer;

  height: var(--tab-height, 38px);
  padding-inline: var(--tab-padding-inline, 15px);

  font-size: var(--tab-font-size, 12px);
  font-weight: 600;
  line-height: 20px;

  color: var(--text-9);
  cursor: pointer;

  &[data-active='true'] {
    color: var(--text-12);
    cursor: default;
  }

  &[aria-disabled='true'] {
    cursor: not-allowed;
    &:hover,
    &:focus-visible {
      color: var(--text-9);
    }
  }

  &:hover,
  &:focus-visible {
    color: var(--text-12);
  }

  ${mediaMinTablet(css`
    font-weight: 700;
  `)}
`

const Indicator = styled(motion.div)`
  position: absolute;

  &[data-position='bottom'] {
    bottom: 0;
  }

  &[data-position='top'] {
    top: 0;
  }

  left: 0;
  right: 0;
  width: 100%;
  height: 2px;
  background: var(--primary-9);
`
